import React, { useState, useEffect, useMemo, useCallback } from 'react';

const PRIMARY = '#0052CC';

const SETTING_META = {
  snmp_trap_port: { label: 'SNMP Trap Port', type: 'number', description: 'Port to listen for SNMP traps from OLTs', icon: '📡' },
  auto_scan_interval: { label: 'Auto Scan Interval (seconds)', type: 'number', description: 'How often to scan OLTs for new ONUs', icon: '🔄' },
  auto_authorize: { label: 'Auto-Authorize New ONUs', type: 'toggle', description: 'Automatically authorize new ONUs using the default preset', icon: '⚡' },
  onu_naming_convention: { label: 'ONU Naming Convention', type: 'text', description: 'Pattern for auto-naming ONUs. Variables: {zone}, {odb}, {port}, {serial}', icon: '🏷️' },
  sync_log_retention_days: { label: 'Sync Log Retention (days)', type: 'number', description: 'How many days to keep sync log records', icon: '📋' },
  telnet_timeout: { label: 'Telnet Timeout (seconds)', type: 'number', description: 'Connection timeout for Telnet sessions to OLTs', icon: '⏱️' },
  snmp_timeout: { label: 'SNMP Timeout (seconds)', type: 'number', description: 'Timeout for SNMP queries', icon: '⏱️' },
  max_concurrent_syncs: { label: 'Max Concurrent Syncs', type: 'number', description: 'Maximum number of OLTs syncing simultaneously', icon: '⚙️' },
};

export default function NOCGeneralPage() {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const api = useMemo(() => {
    const token = localStorage.getItem('session_token');
    const headers = { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) };
    return {
      get: (url) => fetch(url, { headers }).then(r => r.json()),
      put: (url, body) => fetch(url, { method: 'PUT', headers, body: JSON.stringify(body) }).then(r => r.json()),
    };
  }, []);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get('/api/noc/settings');
      const map = {};
      (data.items || []).forEach(s => { map[s.setting_key] = s.setting_value; });
      setSettings(map);
    } catch (e) { setError('Failed to load settings'); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  const handleChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true); setError(''); setSaved(false);
    try {
      const settingsList = Object.entries(settings).map(([k, v]) => ({ key: k, value: v }));
      await api.put('/api/noc/settings', { settings: settingsList });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) { setError('Failed to save settings'); }
    finally { setSaving(false); }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#6B778C' }}>Loading settings...</div>;

  return (
    <div style={{ padding: '24px', background: '#f5f7fa', minHeight: '100vh' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#172B4D' }}>General Settings</h2>
          <p style={{ margin: '4px 0 0', color: '#6B778C', fontSize: 13 }}>NOC Network Module configuration and preferences</p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {saved && <span style={{ color: '#006644', fontWeight: 600, fontSize: 14 }}>✓ Settings saved</span>}
          <button onClick={handleSave} disabled={saving} style={{ background: PRIMARY, color: '#fff', border: 'none', borderRadius: 6, padding: '9px 22px', fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>

      {error && <div style={{ background: '#FFEBE6', border: '1px solid #FF5630', borderRadius: 6, padding: '10px 14px', marginBottom: 16, color: '#BF2600', fontSize: 13 }}>{error}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))', gap: 16 }}>
        {Object.entries(SETTING_META).map(([key, meta]) => (
          <div key={key} style={{ background: '#fff', borderRadius: 10, border: '1px solid #DFE1E6', padding: 20 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <span style={{ fontSize: 24, lineHeight: 1 }}>{meta.icon}</span>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', fontWeight: 700, color: '#172B4D', fontSize: 14, marginBottom: 4 }}>{meta.label}</label>
                <p style={{ margin: '0 0 12px', color: '#6B778C', fontSize: 12 }}>{meta.description}</p>
                {meta.type === 'toggle' ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div
                      onClick={() => handleChange(key, settings[key] === 'true' ? 'false' : 'true')}
                      style={{ width: 44, height: 24, borderRadius: 12, background: settings[key] === 'true' ? PRIMARY : '#DFE1E6', cursor: 'pointer', position: 'relative', transition: 'background 0.2s' }}>
                      <div style={{ position: 'absolute', top: 3, left: settings[key] === 'true' ? 23 : 3, width: 18, height: 18, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 600, color: settings[key] === 'true' ? PRIMARY : '#6B778C' }}>
                      {settings[key] === 'true' ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                ) : (
                  <input
                    type={meta.type === 'number' ? 'number' : 'text'}
                    value={settings[key] || ''}
                    onChange={e => handleChange(key, e.target.value)}
                    style={{ width: '100%', border: '1px solid #DFE1E6', borderRadius: 5, padding: '8px 12px', fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
                  />
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Info box */}
      <div style={{ background: '#DEEBFF', border: '1px solid #B3D4FF', borderRadius: 8, padding: '14px 18px', marginTop: 20 }}>
        <h4 style={{ margin: '0 0 6px', color: '#0747A6', fontSize: 14, fontWeight: 700 }}>ONU Naming Convention Variables</h4>
        <p style={{ margin: 0, color: '#0052CC', fontSize: 13 }}>
          Available variables: <code style={{ background: '#fff', borderRadius: 3, padding: '1px 5px' }}>{'{zone}'}</code> — Zone name &nbsp;|&nbsp;
          <code style={{ background: '#fff', borderRadius: 3, padding: '1px 5px' }}>{'{odb}'}</code> — ODB name &nbsp;|&nbsp;
          <code style={{ background: '#fff', borderRadius: 3, padding: '1px 5px' }}>{'{port}'}</code> — Port number &nbsp;|&nbsp;
          <code style={{ background: '#fff', borderRadius: 3, padding: '1px 5px' }}>{'{serial}'}</code> — ONU serial number
        </p>
        <p style={{ margin: '6px 0 0', color: '#0052CC', fontSize: 12 }}>
          Example: <code style={{ background: '#fff', borderRadius: 3, padding: '1px 5px' }}>{'{zone}-{odb}-{port}'}</code> → <strong>Downtown-ODB01-P03</strong>
        </p>
      </div>
    </div>
  );
}
